import { debounce } from 'lodash-es';
import type { PropType } from 'vue';
import { getSimilarList, getSimilarListByUser } from '~/api';
import type { TagIds, Template } from '~/api/interface';
import MoreIcon from '~/assets/svg/arrowoutlin.svg?url';
import Resource from '~/components/resource';
import { PageKey } from '~/constants/page';
import { SCROLL_LIST } from '~/hooks/useScrollList';
import { getTagIDs } from './info';
import Suggest from './suggest.vue';

const getTagIDsJoinUnderline = (ids?: TagIds, level = 0, pid = '') => {
  if (!ids) {
    return;
  }

  let result = '';
  for (const key in ids) {
    const value = ids[key];
    const parentId = `${level},${key}`;
    if (Array.isArray(value)) {
      value.forEach(v => {
        result += `${pid ? `${pid}:` : ''}${parentId}:${level + 1},${v}_`;
      });
    } else if (typeof value === 'object') {
      result += `${getTagIDsJoinUnderline(value, level + 1, parentId)}_`;
    }
  }
  return result.slice(0, -1);
};

/**
 * 组件关系： detail.tsx -> similar.tsx -> suggest.vue -> cardItem.vue
 */
export const Similar = defineComponent({
  name: 'Similar',
  props: {
    title: {
      type: String,
      default: '',
    },
    cardHeight: {
      type: Number,
      default: 188,
    },
    itemNumber: {
      type: Number,
    },
    hideInfo: {
      type: Boolean,
    },
    active: {
      type: Object as PropType<Template | null>,
      default: () => ({}),
      require: false,
    },
    isBestPractice: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['similarClick'],
  setup(props, { emit }) {
    const suggestRef = ref();
    const { $t } = useNuxtApp();

    const resourceLocation = injectResourceLocation();

    const getSimilarData = async (params: any) =>
      await getSimilarList({
        ...params,
        template_id: props.active?.id,
        ...(props.isBestPractice && {
          is_personal_excellent_template: 1,
        }),
      });

    const getSimilarDataByUser = async (params: any) =>
      await getSimilarListByUser({
        ...params,
        many: 1,
        // is_personal_excellent_template: 1,
      });

    const onItemClick = (data: Template, list: Template[]) => {
      emit('similarClick', list, data);
    };

    watch(
      () => props.active,
      debounce(() => {
        suggestRef.value?.reload();
      }, 500),
    );

    const router = useRouter();

    function toMore(tag_ids?: string) {
      trackEvent(TRACK_EVENT.CLICK_TEMPLATE_MORE, {
        source: resourceLocation?.source,
        template_id: props.active?.id,
        template_title: props.active?.name,
        tags: getTagIDs(props.active?.tag_ids)
          .map(item => item.tag_name)
          .join(','),
      });

      const open = useTemplateVisible();
      open.value = false;

      router.push({ name: PageKey.TEMPLATE, query: { tag_ids } });
    }

    return () => (
      <>
        {props.title ? (
          <div class="flex-between mb-[5px]">
            <span class="text-[16px] leading-[1] text-font-1 font-bold">{props.title}</span>
            {isGlobalVersion() ? null : (
              <a-button
                type="text"
                class="more-btn h-[16px]"
                onClick={() => toMore(getTagIDsJoinUnderline(props.active?.tag_ids))}
              >
                {$t('cha-kan-geng-duo')}
                <Resource src={MoreIcon} class="w-[16px] h-[16px] opacity-30" />
              </a-button>
            )}
          </div>
        ) : null}
        <Suggest
          ref={suggestRef}
          id={SCROLL_LIST.SIMILAR_TEMPLATE}
          api={!props.isBestPractice ? getSimilarData : getSimilarDataByUser}
          collect={false}
          share={false}
          itemHeight={props.cardHeight}
          filterId={props.active?.id}
          hideInfo={props.hideInfo}
          small={true}
          itemNumber={props.itemNumber}
          templateSource={TEMPLATE_SOURCE.SIMILAR_RECOM}
          onItemClick={onItemClick}
          isBestPractice={props.isBestPractice}
        />
      </>
    );
  },
});
